<script setup lang="ts">
import { Head, Link, router, useForm } from "@inertiajs/vue3"
import { Search, SlidersHorizontal, Users, X } from "lucide-vue-next"
import { Pencil, Plus, Trash2 } from "lucide-vue-next"
import { computed, onUnmounted, ref, watch } from "vue"


import AddressForm, { type AddressFields } from "@/components/AddressForm.vue"
import AppDataTable from "@/components/AppDataTable.vue"
import type { TableColumn } from "@/components/AppDataTable.vue"
import AppDialog from "@/components/AppDialog.vue"
import AppPagination from "@/components/AppPagination.vue"
import FormSection from "@/components/FormSection.vue"
import InputError from "@/components/InputError.vue"
import RelativeTime from "@/components/RelativeTime.vue"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { useAuth } from "@/composables/useAuth"
import { useDialog } from "@/composables/useDialog"
import AppLayout from "@/layouts/AppLayout.vue"
import { formatLabel } from "@/lib/utils"
import {
  editPartnerPath,
  partnerPath,
  partnersPath,
} from "@/routes"
import { type BreadcrumbItem } from "@/types"

interface Organization {
  id: number
  name: string
}

interface Partner {
  id: number
  name: string
  contact: string
  email: string
  phone: string
  active: boolean
  created_at: string
  organization: Organization
}

interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Filters {
  q?: string | null
  organization_id?: string | null
  active?: string | null
  sort?: string | null
  direction?: string | null
}

const props = defineProps<{
  partners: Partner[]
  organizations: Organization[]
  pagination: PaginationMeta
  filters: Filters
  partner_categories: Record<string, string[]>
  partner_types: string[]
}>()

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Partners", href: partnersPath() },
]

// ── Search & filter state (initialised from server-rendered props) ─────────
// Use undefined (not "") so shadcn Select shows its placeholder correctly.
const ALL = "__all__"

const search = ref(props.filters.q ?? "")
const orgFilter = ref(props.filters.organization_id ?? ALL)
const activeFilter = ref(props.filters.active ?? ALL)
const sortCol = ref(props.filters.sort ?? "")
const sortDir = ref<"asc" | "desc">((props.filters.direction as "asc" | "desc") ?? "desc")

const hasActiveFilters = computed(
  () => search.value !== "" || orgFilter.value !== ALL || activeFilter.value !== ALL,
)

const filterParams = computed(() => ({
  q: search.value || undefined,
  organization_id: orgFilter.value !== ALL ? orgFilter.value : undefined,
  active: activeFilter.value !== ALL ? activeFilter.value : undefined,
  sort: sortCol.value || undefined,
  direction: sortCol.value ? sortDir.value : undefined,
}))

const tableColumns: TableColumn[] = [
  { key: "name", label: "Name", sortable: true },
  { key: "organization_name", label: "Organization", sortable: true },
  { key: "contact", label: "Contact" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "status", label: "Status" },
  { key: "created_at", label: "Created", sortable: true },
  { key: "actions", label: "Actions", align: "right" },
]

let searchTimer: ReturnType<typeof setTimeout>

function applyFilters() {
  const params = Object.fromEntries(
    Object.entries(filterParams.value).filter(([, v]) => v != null),
  )
  router.get(partnersPath(), params, { preserveState: true, replace: true })
}

onUnmounted(() => clearTimeout(searchTimer))

// Debounce the text search; apply filters immediately on dropdown changes
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(applyFilters, 350)
})

watch([orgFilter, activeFilter], applyFilters)

function clearFilters() {
  search.value = ""
  orgFilter.value = ALL
  activeFilter.value = ALL
  sortCol.value = ""
  sortDir.value = "desc"
}

function handleSort(key: string, dir: "asc" | "desc") {
  sortCol.value = key
  sortDir.value = dir
  applyFilters()
}

// ── Create dialog ─────────────────────────────────────────────────────────
const { isAdmin } = useAuth()
const { confirm } = useDialog()

const showCreateDialog = ref(false)

const form = useForm({
  name: "",
  contact: "",
  email: "",
  phone: "",
  organization_id: "" as string | number,
  active: true,
  partner_type: "",
  categories: [] as string[],
  address_attributes: {
    id: undefined as number | undefined,
    unit_no: "",
    street_no: "",
    street_name: "",
    street_type: "",
    city: "",
    state: "",
    postcode: "",
  },
})

const createAvailableCategories = computed(() => props.partner_categories[form.partner_type] ?? [])

watch(
  () => form.partner_type,
  () => { form.categories = [] },
)

function toggleCreateCategory(cat: string, checked: boolean) {
  if (checked) {
    form.categories = [...form.categories, cat]
  } else {
    form.categories = form.categories.filter((c) => c !== cat)
  }
}

function openCreate() {
  form.reset()
  showCreateDialog.value = true
}

function submitCreate() {
  form.post(partnersPath(), {
    preserveScroll: true,
    onSuccess: () => {
      showCreateDialog.value = false
    },
  })
}

const createAddressFields = computed<AddressFields>({
  get: () => ({
    unit_no: form.address_attributes.unit_no,
    street_no: form.address_attributes.street_no,
    street_name: form.address_attributes.street_name,
    street_type: form.address_attributes.street_type,
    city: form.address_attributes.city,
    state: form.address_attributes.state,
    postcode: form.address_attributes.postcode,
  }),
  set: (val) => Object.assign(form.address_attributes, val),
})

// ── Delete ────────────────────────────────────────────────────────────────
async function confirmDelete(partner: Partner) {
  const ok = await confirm({
    title: 'Delete partner?',
    description: `Are you sure you want to delete ${partner.name}? This action cannot be undone.`,
    confirmLabel: 'Delete',
  })
  if (!ok) return
  router.delete(partnerPath(partner.id), { preserveScroll: true })
}

function err(field: string): string[] | undefined {
  const msg = (form.errors as unknown as Record<string, string | undefined>)[field]
  return msg ? [msg] : undefined
}
</script>

<template>
  <Head title="Partners" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="flex h-full flex-1 flex-col gap-4 overflow-x-auto p-4">
      <!-- Page header -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Users class="text-muted-foreground size-6" />
          <div>
            <h1 class="text-foreground text-xl font-semibold">Partners</h1>
            <p class="text-muted-foreground text-sm">Manage your partners</p>
          </div>
        </div>
        <Button @click="openCreate">
          <Plus class="size-4" />
          New Partner
        </Button>
      </div>

      <!-- Table card -->
      <Card class="border-sidebar-border/70 dark:border-sidebar-border">
        <CardHeader class="pb-3">
          <div class="flex items-center justify-between">
            <div>
              <CardTitle class="text-base">All Partners</CardTitle>
              <CardDescription>
                {{ pagination.total_count }} partner{{
                  pagination.total_count !== 1 ? "s" : ""
                }}{{ hasActiveFilters ? " found" : " total" }}
              </CardDescription>
            </div>
            <SlidersHorizontal class="text-muted-foreground size-4" />
          </div>
        </CardHeader>

        <!-- Search & filters -->
        <div class="border-border border-b px-4 pb-4">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
            <!-- Search -->
            <div class="relative flex-1">
              <Search class="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                v-model="search"
                placeholder="Search by name, contact or email…"
                class="pl-9"
              />
            </div>

            <!-- Organization filter -->
            <Select v-model="orgFilter">
              <SelectTrigger class="w-full sm:w-48">
                <SelectValue placeholder="All organizations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem :value="ALL">All organizations</SelectItem>
                <SelectItem
                  v-for="org in organizations"
                  :key="org.id"
                  :value="String(org.id)"
                >{{ org.name }}</SelectItem>
              </SelectContent>
            </Select>

            <!-- Status filter -->
            <Select v-model="activeFilter">
              <SelectTrigger class="w-full sm:w-36">
                <SelectValue placeholder="Any status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem :value="ALL">Any status</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>

            <!-- Clear filters -->
            <Button
              v-if="hasActiveFilters"
              variant="ghost"
              size="sm"
              class="text-muted-foreground hover:text-foreground shrink-0"
              @click="clearFilters"
            >
              <X class="size-4" />
              Clear
            </Button>
          </div>
        </div>

        <CardContent class="p-0">
          <!-- Empty state -->
          <div
            v-if="partners.length === 0"
            class="flex flex-col items-center justify-center gap-3 py-16 text-center"
          >
            <Users class="text-muted-foreground/40 size-12" />
            <p class="text-muted-foreground text-sm">
              {{ hasActiveFilters ? "No partners match your filters" : "No partners yet" }}
            </p>
            <Button v-if="hasActiveFilters" variant="outline" size="sm" @click="clearFilters">
              <X class="size-4" />
              Clear filters
            </Button>
            <Button v-else variant="outline" size="sm" @click="openCreate">
              <Plus class="size-4" />
              Add your first partner
            </Button>
          </div>

          <!-- Table -->
          <AppDataTable
            v-else
            :columns="tableColumns"
            :sort="sortCol || undefined"
            :direction="sortDir"
            @sort="handleSort"
          >
            <tr
              v-for="partner in partners"
              :key="partner.id"
              class="border-border hover:bg-muted/50 border-b transition-colors last:border-0"
            >
              <td class="px-6 py-4 font-medium">
                <Link
                  :href="editPartnerPath(partner.id)"
                  class="text-foreground hover:text-primary hover:underline"
                >{{ partner.name }}</Link>
              </td>
              <td class="text-muted-foreground px-6 py-4">{{ partner.organization.name }}</td>
              <td class="text-muted-foreground px-6 py-4">{{ partner.contact }}</td>
              <td class="text-muted-foreground px-6 py-4">{{ partner.email }}</td>
              <td class="text-muted-foreground px-6 py-4">{{ partner.phone }}</td>
              <td class="px-6 py-4">
                <Badge :variant="partner.active ? 'default' : 'secondary'">
                  {{ partner.active ? "Active" : "Inactive" }}
                </Badge>
              </td>
              <td class="text-muted-foreground px-6 py-4">
                <RelativeTime :date="partner.created_at" />
              </td>
              <td class="px-6 py-4">
                <div class="flex items-center justify-end gap-2">
                  <Button variant="ghost" size="icon" class="size-8" as-child>
                    <Link :href="editPartnerPath(partner.id)">
                      <Pencil class="size-4" />
                      <span class="sr-only">Edit</span>
                    </Link>
                  </Button>
                  <Button
                    v-if="isAdmin"
                    variant="ghost"
                    size="icon"
                    class="text-destructive hover:text-destructive size-8"
                    @click="confirmDelete(partner)"
                  >
                    <Trash2 class="size-4" />
                    <span class="sr-only">Delete</span>
                  </Button>
                </div>
              </td>
            </tr>
          </AppDataTable>

          <!-- Pagination -->
          <AppPagination
            v-if="pagination.total_pages > 1"
            :current-page="pagination.current_page"
            :total-pages="pagination.total_pages"
            :total-count="pagination.total_count"
            :per-page="pagination.per_page"
            :base-url="partnersPath()"
            :extra-params="filterParams"
          />
        </CardContent>
      </Card>
    </div>

    <!-- ── Create Dialog ─────────────────────────────────────────────────── -->
    <AppDialog
      v-model:open="showCreateDialog"
      title="New Partner"
      description="Fill in the details to create a new partner."
      scrollable
      class="sm:max-w-lg"
    >
      <div class="space-y-4">
        <FormSection title="Contact Information">
          <div class="grid gap-2">
            <Label for="c-name">Name</Label>
            <Input id="c-name" v-model="form.name" placeholder="John Smith" />
            <InputError :messages="err('name')" />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div class="grid gap-2">
              <Label for="c-contact">Contact Person</Label>
              <Input id="c-contact" v-model="form.contact" placeholder="John Smith" />
              <InputError :messages="err('contact')" />
            </div>
            <div class="grid gap-2">
              <Label for="c-phone">Phone</Label>
              <Input id="c-phone" v-model="form.phone" type="tel" placeholder="+61 400 000 000" />
              <InputError :messages="err('phone')" />
            </div>
          </div>

          <div class="grid gap-2">
            <Label for="c-email">Email</Label>
            <Input id="c-email" v-model="form.email" type="email" placeholder="john@example.com" />
            <InputError :messages="err('email')" />
          </div>

          <div class="grid gap-2">
            <Label for="c-organization">Organization</Label>
            <Select v-model="form.organization_id">
              <SelectTrigger class="w-full" id="c-organization">
                <SelectValue placeholder="Select organization" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  v-for="org in organizations"
                  :key="org.id"
                  :value="org.id"
                >{{ org.name }}</SelectItem>
              </SelectContent>
            </Select>
            <InputError :messages="err('organization_id')" />
          </div>
        </FormSection>

        <FormSection title="Service Type">
          <div class="grid gap-2">
            <Label for="c-type">Type</Label>
            <Select v-model="form.partner_type">
              <SelectTrigger class="w-full" id="c-type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="t in props.partner_types" :key="t" :value="t">{{ formatLabel(t) }}</SelectItem>
              </SelectContent>
            </Select>
            <InputError :messages="err('partner_type')" />
          </div>

          <div v-if="createAvailableCategories.length > 0" class="grid gap-2">
            <Label>Categories</Label>
            <p class="text-muted-foreground text-xs">Select all service categories this partner offers.</p>
            <div class="grid grid-cols-2 gap-y-2.5 gap-x-4">
              <div
                v-for="cat in createAvailableCategories"
                :key="cat"
                class="flex items-center gap-2"
              >
                <Checkbox
                  :id="`c-cat-${cat}`"
                  :model-value="form.categories.includes(cat)"
                  @update:model-value="toggleCreateCategory(cat, $event as boolean)"
                />
                <Label :for="`c-cat-${cat}`" class="font-normal">{{ formatLabel(cat) }}</Label>
              </div>
            </div>
            <InputError :messages="err('categories')" />
          </div>
        </FormSection>

        <div class="border-border flex items-center justify-between rounded-lg border p-4">
          <div>
            <p class="text-sm font-medium">Active</p>
            <p class="text-muted-foreground text-xs">Allow this partner to submit loan applications.</p>
          </div>
          <Switch id="c-active" :checked="form.active" @update:checked="form.active = $event" />
        </div>

        <div class="border-border space-y-3 rounded-lg border p-4">
          <p class="text-muted-foreground text-xs font-medium uppercase tracking-wide">
            Address <span class="normal-case">(optional)</span>
          </p>
          <AddressForm v-model="createAddressFields" :errors="form.errors" error-prefix="address." />
        </div>
      </div>
      <template #footer>
        <Button variant="outline" @click="showCreateDialog = false">Cancel</Button>
        <Button :disabled="form.processing" @click="submitCreate">Create Partner</Button>
      </template>
    </AppDialog>

  </AppLayout>
</template>
